import { Injectable } from '@angular/core';
import { TextToSpeech } from '@capacitor-community/text-to-speech';
import Speech from 'speak-tts';
import { MapService } from './map.service';
import { Platform } from '@ionic/angular';

declare const playSound: any;

@Injectable({
    providedIn: 'root'
})
export class SpeechService {
    speechVoice = new Speech();
    cachedVoices: SpeechSynthesisVoice[] = [];

    constructor(
        public mapService: MapService,
        private platform: Platform
    ) {
        console.log(this.platform.platforms());
        this.initVoices();
    }

    private initVoices() {
        if (typeof window !== 'undefined' && 'speechSynthesis' in window && window.speechSynthesis) {
            this.cachedVoices = window.speechSynthesis.getVoices();
            if (typeof window.speechSynthesis.onvoiceschanged !== 'undefined') {
                window.speechSynthesis.onvoiceschanged = () => {
                    this.cachedVoices = window.speechSynthesis.getVoices();
                };
            }
        }
    }

    async speechWordInit(option: any) {
        if (this.speechVoice.hasBrowserSupport()) {
            this.speechVoice._fetchVoices().then((val: any) => {
                const valEng = val.filter((it: any) => it.lang === 'en-US');
                console.log('speech synthesis supported', valEng);
            });
            await this.speechVoice.init(option);
        } else {
            console.log('Browser not support speech');
        }
    }

    speechText(word: string, speed: number) {
        if (!word) {
            console.log('Text not found');
            return;
        }

        const cleanWord = word.trim().toLowerCase();

        // Unlock Web Audio Context and SpeechSynthesis on iOS if available
        if (typeof (window as any).unlockiOSAudio === 'function') {
            (window as any).unlockiOSAudio();
        }

        const isHybrid = this.platform.is('capacitor') || this.platform.is('cordova') || this.platform.is('hybrid');

        // 1. Native Mobile App (Capacitor / Cordova app on iOS or Android)
        if (isHybrid) {
            console.log('Speech voice Native Mobile (Capacitor/Cordova), speed:', speed);
            const option = {
                text: cleanWord,
                locale: 'en_US',
                pitchRate: 1.0,
                speechRate: speed || 1.0,
                volume: 1.0,
                category: 'ambient',
            };
            TextToSpeech.speak(option).then(() => {
                console.log('Native TextToSpeech Success !');
            }).catch(e => {
                console.error('Native TextToSpeech error:', e);
            });
            return;
        }

        // 2. Web Speech API (iOS Safari, Mobile Chrome, Desktop Browsers)
        try {
            if ('speechSynthesis' in window && window.speechSynthesis) {
                if (window.speechSynthesis.paused) {
                    window.speechSynthesis.resume();
                }

                const utterance = new SpeechSynthesisUtterance(cleanWord);
                utterance.lang = 'en-US';
                utterance.rate = speed || 1.0;
                utterance.pitch = 1.0;
                utterance.volume = 1.0;

                let voices = this.cachedVoices;
                if (!voices || voices.length === 0) {
                    voices = window.speechSynthesis.getVoices();
                    if (voices && voices.length > 0) {
                        this.cachedVoices = voices;
                    }
                }

                if (voices && voices.length > 0) {
                    const enVoice = voices.find(v => v.lang === 'en-US' || v.lang === 'en_US' || (v.lang && v.lang.startsWith('en')));
                    if (enVoice) {
                        utterance.voice = enVoice;
                    }
                }

                window.speechSynthesis.speak(utterance);
                console.log('Native SpeechSynthesis spoken successfully:', cleanWord);
                return;
            }
        } catch (e) {
            console.log('Native SpeechSynthesis error, trying fallback:', e);
        }

        if (this.mapService.getPlatformIsDesktop()) {
            try {
                const option = {
                    volume: 1,
                    lang: 'en-US',
                    rate: speed,
                    pitch: 1,
                    splitSentences: true,
                };
                console.log('Speech voice Desktop, speed:', option);
                this.speechWordInit(option).catch(e => console.log('Speech voice Desktop Init error', e));
            } catch (e) {
                console.log('Speech voice Desktop Init error', e);
            }
            this.speechVoice.speak({
                text: cleanWord,
                queue: false,
            }).then((data) => {
                console.log('Speech Desktop Success !');
            }).catch(e => {
                console.error('An error occurred :', e);
            });
        }
    }
}
